import {
  zoomTransform,
  select,
  event,
  format,
  timeFormat,
  line as lineD3,
  area as areaD3,
} from "d3";
import {
  grayLightBackground,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import {
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  createTooltips,
  displayDataTA,
  inRange,
  mouseDate,
  roundDate,
  zoomSetup,
} from "@/_core/charts/TechnicalAnalysis";

export function STOCHHover(_this, upColor, dnColor, colorTheme) {
  const auxName = "STOCH";
  let valueLast = _this.candleData[_this.candleData.length - 1].stochPK;
  let xTooltipWidth = 80;
  let yTooltip = createTooltips(
    _this.svgNodeSTOCH,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    _this.colorTheme,
    auxName,
  );
  let lastYTooltip = yTooltip["lastTooltip"];
  _this.svgNodeSTOCH.lastYTooltip = lastYTooltip;
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(
    _this.svgNodeSTOCH,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  vLineCreate(
    _this.svgNodeSTOCH,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  dotCreate(
    _this.svgNodeSTOCH,
    "StochPK" + _this.chartName,
    3.5,
    textactive2(colorTheme),
    upColor,
    2,
  );
  dotCreate(
    _this.svgNodeSTOCH,
    "StochPD" + _this.chartName,
    3.5,
    textactive2(colorTheme),
    dnColor,
    2,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName + _this.chartName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(
        event.layerX - _this.svgNodeSTOCH.margin.left,
        _this.svgNodeSTOCH.width,
      ),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNodeSTOCH.x);
    var yt = _this.svgNodeSTOCH.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.candleData[_this.candleData.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.candleData);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.candleData[_this.candleData.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(
        event.layerY - _this.svgNodeSTOCH.margin.top,
        _this.svgNodeSTOCH.height,
      ),
      0,
    );
    let yRate = yt.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style(
        "left",
        _this.svgNodeSTOCH.width + _this.svgNodeSTOCH.margin.left + 1 + "px",
      )
      .style("top", yPixels + _this.svgNodeSTOCH.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));

    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) +
          _this.svgNodeSTOCH.margin.left -
          xTooltipWidth / 2 +
          "px",
      )
      .style(
        "top",
        _this.svgNodeSTOCH.height + _this.svgNodeSTOCH.margin.top + 2 + "px",
      )
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineSTOCH" + _this.chartName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);
    select(".vHoverLineTA" + _this.chartName)
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }
    if (_this.showMACD === true) {
      select(".vHoverLineMACD" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }
    select(".vHoverLineSTOCH" + _this.chartName)
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    if (highlightCandles) {
      let bandWidth = _this.transform.k * _this.bandWidth;
      const candleWidth = bandWidth * _this.barWidth + 1;
      const yt2 = _this.svgNode.y;
      if (_this.isCandles) {
        let up = Math.max(selData.open, selData.close);
        let dn = Math.min(selData.open, selData.close);
        select(".cndlStckTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt2(selData.high))
          .attr("y2", yt2(up));
        select(".cndlStck2TA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt2(dn))
          .attr("y2", yt2(selData.low));
        select(".cndlTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", yt2(up))
          .attr("height", Math.max(1, yt2(dn) - yt2(up)))
          .attr("width", candleWidth);
      } else {
        select(".dotTA" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt2(selData.close)})`,
          );
      }
      if (_this.showRSI === true) {
        const yt4 = _this.svgNodeRSI.y;
        select(".dotRSI" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt4(selData.rsi)})`,
          );
      }
      if (_this.showMACD === true) {
        const yt3 = _this.svgNodeMACD.y;
        select(".dotMACD" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt3(selData.macd)})`,
          );
        select(".dotMACDSignal" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt3(selData.macdSignal)})`,
          );
        select(".barMACDHistogram" + _this.chartName)
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", Math.min(yt3(0), yt3(selData["macdHist"])))
          .attr(
            "height",
            Math.max(1, Math.abs(yt3(0) - yt3(selData["macdHist"]))),
          )
          .attr("width", candleWidth);
      }
      select(".dotStochPK" + _this.chartName)
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData.stochPK)})`,
        );
      select(".dotStochPD" + _this.chartName)
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData.stochPD)})`,
        );
    } else {
      if (_this.isCandles) {
        select(".cndlStckTA" + _this.chartName).style("opacity", 0);
        select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
        select(".cndlTA" + _this.chartName).style("opacity", 0);
      } else {
        select(".dotTA" + _this.chartName).style("opacity", 0);
      }
      if (_this.showRSI === true) {
        select(".dotRSI" + _this.chartName).style("opacity", 0);
      }
      if (_this.showMACD === true) {
        select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
        select(".dotMACD" + _this.chartName).style("opacity", 0);
        select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
      }
      select(".dotStochPK" + _this.chartName).style("opacity", 0);
      select(".dotStochPD" + _this.chartName).style("opacity", 0);
    }

    displayDataTA(selData, _this);
  }
  function mouseout() {
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    if (_this.showMACD === true) {
      select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
      select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    }
    select(".hHoverLineSTOCH" + _this.chartName).style("opacity", 0);
    select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
    select(".dotStochPK" + _this.chartName).style("opacity", 0);
    select(".dotStochPD" + _this.chartName).style("opacity", 0);
    displayDataTA(_this.candleData[_this.candleData.length - 1], _this);
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    if (_this.showMACD === true) {
      select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
      select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    }
    select(".hHoverLineSTOCH" + _this.chartName).style("opacity", 0);
    select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
    select(".dotStochPK" + _this.chartName).style("opacity", 0);
    select(".dotStochPD" + _this.chartName).style("opacity", 0);

    _this.transform = event.transform;
    _this.svgNodeSTOCH.gX.call(
      _this.svgNodeSTOCH.xAxis.scale(
        _this.transform.rescaleX(_this.svgNodeSTOCH.x),
      ),
    );
    const xt = _this.transform.rescaleX(_this.svgNodeSTOCH.x);
    STOCHZooming(_this.transform, xt, _this);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentTA" + _this.chartName).call(
        _this.zoomTA.transform,
        _this.transform,
      );
      if (_this.showRSI) {
        select(".hoverParentRSI" + _this.chartName).call(
          _this.zoomRSI.transform,
          _this.transform,
        );
      }
      if (_this.showMACD) {
        select(".hoverParentMACD" + _this.chartName).call(
          _this.zoomMACD.transform,
          _this.transform,
        );
      }
      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNodeSTOCH.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNodeSTOCH.width));

      if (!_this.externalZoom) {
        let data = _this.candleData.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }

      let yt = _this.svgNodeSTOCH.y;
      STOCHZoomEnd(
        _this.transform,
        xt,
        yt,
        _this.filteredData,
        _this,
        lastYTooltip,
        valueLast,
      );
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNode" + auxName,
    "zoom" + auxName,
    auxName,
    _this.chartName,
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
}

export function STOCHZooming(t, xt, _this) {
  let area, line, fieldName;
  // stoch
  if (_this.showSTOCH === true) {
    fieldName = "stochPK";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeSTOCH.y(d[fieldName]));
    _this.svgNodeSTOCH["upperLineStochPK" + _this.chartName].attr("d", (d) =>
      line(d),
    );
    fieldName = "stochPD";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeSTOCH.y(d[fieldName]));
    _this.svgNodeSTOCH["lowerLineStochPD" + _this.chartName].attr("d", (d) =>
      line(d),
    );
    area = areaD3()
      .defined((d) => !Number.isNaN(d["stochPK"]))
      .defined((d) => !Number.isNaN(d["stochPD"]))
      .x((d) => xt(d.date))
      .y0((d) => _this.svgNodeSTOCH.y(d["stochPK"]))
      .y1((d) => _this.svgNodeSTOCH.y(d["stochPD"]));
    _this.svgNodeSTOCH["areaStoch" + _this.chartName].attr("d", area);
  }
}

export function STOCHZoomEnd(
  t,
  xt,
  yt,
  filtered,
  _this,
  lastYTooltip,
  valueLast,
) {
  let line, area, fieldName;
  // stoch
  if (_this.showSTOCH === true) {
    fieldName = "stochPK";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeSTOCH.y(d[fieldName]));
    _this.svgNodeSTOCH["upperLineStochPK" + _this.chartName]
      .data([filtered])
      .join("path")
      .attr("d", (d) => line(d));
    fieldName = "stochPD";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeSTOCH.y(d[fieldName]));
    _this.svgNodeSTOCH["lowerLineStochPD" + _this.chartName]
      .data([filtered])
      .join("path")
      .attr("d", (d) => line(d));
    area = areaD3()
      .defined((d) => !Number.isNaN(d["stochPK"]))
      .defined((d) => !Number.isNaN(d["stochPD"]))
      .x((d) => xt(d.date))
      .y0((d) => _this.svgNodeSTOCH.y(d["stochPK"]))
      .y1((d) => _this.svgNodeSTOCH.y(d["stochPD"]));
    _this.svgNodeSTOCH["areaStoch" + _this.chartName]
      .data([filtered])
      .join("path")
      .attr("d", area);
    // last rate
    var yMax = yt.invert(0);
    var yMin = yt.invert(_this.svgNodeSTOCH.height);
    lastYTooltip
      .html(format(_this.currencyFormat)(valueLast))
      .transition()
      .duration(500)
      .style("opacity", yMin <= valueLast && valueLast <= yMax ? 1 : 0)
      .style("top", yt(valueLast) + _this.svgNodeSTOCH.margin.top - 11 + "px");
  }
}
